
	import Vue, { PropType } from 'vue';
	import Component from 'vue-class-component';

	import { ComponentMap, Field } from '@/types';
	import { mdiCheckboxBlankOutline, mdiCheckboxMarked } from '@mdi/js';
	import getRules, { getRulesFn } from '@/mixins/ValidationRules';

	import {
		VAutocomplete,
		VSelect
	} from 'vuetify/lib';

	const Props = Vue.extend({
		props: {
			field: {
				type: Object as PropType<Field>,
				required: true
			},
			fieldName: {
				type: String,
				required: true
			}
		}
	});

	/** Form field to select a value from a list */
	@Component<SelectField>({
		mixins: [
			getRules
		],
		model: {
			prop: 'field',
			event: 'change'
		},
		components: {
			VSelect,
			VAutocomplete
		}
	})
	export default class SelectField extends Props {

		/** The list of components to display by type of field */
		componentMap: ComponentMap = {
			select: 'VSelect',
			autocomplete: 'VAutocomplete'
		};

		checkboxMarked = mdiCheckboxMarked;
		checkboxBlankOutline = mdiCheckboxBlankOutline;
		iconSelectAll = mdiCheckboxBlankOutline;

		getRules!: getRulesFn;

		created() {
			if (this.field.options && !this.field.options.multiple) {
				return;
			}

			if (!this.field.value) {
				return this.field.value = [];
			}

			if (this.isFull()) {
				this.setIconSelectAll();
			}
		}

		/** Get the component name for 'is' prop */
		getComponentName(type: string): string {
			return this.componentMap[type];
		}

		isFull(): boolean {
			return Array.isArray(this.field.value) && this.field.items.length === this.field.value.length;
		}

		setIconSelectAll(): void {
			if (this.isFull()) {
				this.iconSelectAll = this.checkboxMarked;

				return;
			}

			this.iconSelectAll = this.checkboxBlankOutline;
		}

		toggle(): void {
			this.$nextTick(() => {
				if (this.isFull()) {
					this.field.value = [];
					this.setIconSelectAll();
					this.emitChangeEvent(this.field, this.fieldName);

					return;
				}

				const listItems = this.field.items.map((item: any) => item.value);
				this.field.value = listItems.slice();
				this.emitChangeEvent(this.field, this.fieldName);
				this.setIconSelectAll();
			});
		}

		/**
		 * Update the v-model by emetting 'change' event
		 * Also emits 'refresh' event if the field is dynamic
		 */
		emitChangeEvent(field: Field, fieldName: string): void {
			this.setIconSelectAll();

			if (true === this.field.dynamic) {
				this.disableAllOptions();
			}

			this.$emit('change', field, fieldName);
		}

		/**
		 * Disable dynamic field options until refreshed
		 */
		disableAllOptions(): void {
			this.field.items = this.field.items.map((item: object) => (
				{
					...item,
					disabled: true
				}));
		}

		/**
		 * Catch enter key events
		 * Allows to do custom action per field
		 * and prevent default HTML <form> behavior
		 * when enter is pressed
		 */
		emitEnterEvent(fieldName: string): void {
			this.$emit('enter', fieldName);
		}

		/**
		 * Transform the rules from the field
		 * By default it's an array of objects describing the rules,
		 * we transform this to an array of functions
		 */
		getValidationRules() {
			// If we don't have rules, return an empty array
			if (!this.field.rules && !this.field.rules) {
				return [];
			}

			return this.getRules(this.field.rules);
		}
	}
